import { Directive, ElementRef, inject, OnDestroy } from '@angular/core';
import { IqEventBusBrokerService } from '@dat/iq-event-bus';
import {
    AddClassPayload,
    AddClassPayloadSchema,
    RemoveClassPayload,
    RemoveClassPayloadSchema,
    ToggleClassPayload,
    ToggleClassPayloadSchema,
} from './style-actions.models';

@Directive({
    selector: '[iqStyleProcessorStyleActions]',
    standalone: true,
})
export class StyleActionsDirective implements OnDestroy {
    private _eventBus: IqEventBusBrokerService = inject(
        IqEventBusBrokerService,
    );

    private _unregisteredActions: Record<string, any> = {};

    private elementRef: ElementRef = inject(ElementRef);
    constructor() {
        this._unregisteredActions = this._eventBus.registerMap({
            addClass: {
                callback: (payload: AddClassPayload) => {
                    this.addClass(payload);
                },
            },
            removeClass: {
                callback: (payload: RemoveClassPayload) => {
                    this.removeClass(payload);
                },
            },
            toggleClass: {
                callback: (payload: ToggleClassPayload) => {
                    this.toggleClass(payload);
                },
            },
        });
    }

    ngOnDestroy() {
        this._eventBus.unregisterMap(this._unregisteredActions);
    }

    addClass(payload: AddClassPayload): void {
        const test: any = AddClassPayloadSchema.safeParse(payload);

        if (!test.success) {
            console.warn('AddClassPayloadSchema Invalid Payload');
            console.log(test.error.issues);
            return;
        }

        payload?.selector?.length
            ? this._addToSingleSelector(payload.selector, payload.classNames)
            : this._addToAllElements(payload.cssSelectors, payload.classNames);
    }

    private _addToAllElements(
        cssSelectors: string[] = [],
        classNames: string[],
    ) {
        cssSelectors.forEach((selector: string) => {
            this._addToSingleSelector(selector, classNames);
        });
    }

    private _addToSingleSelector(selector: string, classNames: string[]) {
        const elements = selector
            ? this.elementRef.nativeElement.querySelectorAll(selector)
            : [this.elementRef.nativeElement];

        elements.forEach((element: HTMLElement) => {
            classNames?.forEach((className: string) => {
                element.classList.add(className);
            });
        });
    }

    removeClass(payload: RemoveClassPayload): void {
        const test: any = RemoveClassPayloadSchema.safeParse(payload);
        if (!test.success) {
            console.warn('RemoveClassPayloadSchema Invalid Payload');
            console.log(test.error.issues);
            return;
        }

        payload?.selector?.length
            ? this._removeFromSingleSelector(
                  payload.selector,
                  payload.classNames,
              )
            : this._removeFromAllElements(
                  payload.cssSelectors,
                  payload.classNames,
              );
    }
    private _removeFromAllElements(
        cssSelectors: string[] = [],
        classNames: string[],
    ) {
        cssSelectors.forEach((selector: string) => {
            this._removeFromSingleSelector(selector, classNames);
        });
    }
    private _removeFromSingleSelector(selector: string, classNames: string[]) {
        const elements = selector
            ? this.elementRef.nativeElement.querySelectorAll(selector)
            : [this.elementRef.nativeElement];

        elements.forEach((element: HTMLElement) => {
            classNames?.forEach((className: string) => {
                element.classList.remove(className);
            });
        });
    }

    toggleClass(payload: ToggleClassPayload): void {
        const test: any = ToggleClassPayloadSchema.safeParse(payload);
        if (!test.success) {
            console.warn('ToggleClassPayloadSchema Invalid Payload');
            console.log(test.error.issues);
            return;
        }

        payload?.selector?.length
            ? this._toggleSingleSelector(payload.selector, payload.classNames)
            : this._toggleAllElements(payload.cssSelectors, payload.classNames);
    }

    private _toggleAllElements(
        cssSelectors: string[] = [],
        classNames: string[],
    ) {
        cssSelectors.forEach((selector: string) => {
            this._toggleSingleSelector(selector, classNames);
        });
    }

    private _toggleSingleSelector(selector: string, classNames: string[]) {
        const elements = selector
            ? this.elementRef.nativeElement.querySelectorAll(selector)
            : [this.elementRef.nativeElement];

        elements.forEach((element: HTMLElement) => {
            classNames?.forEach((className: string) => {
                element.classList.contains(className)
                    ? element.classList.remove(className)
                    : element.classList.add(className);
            });
        });
    }
}
